import { Box, Flex } from '@rebass/grid';
import React, { ButtonHTMLAttributes, FC, memo, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import format from 'date-fns/format';
import { getAssignmentColor, getFontCss, Theme } from '../../constants/theme';
import { UnreachableCaseError } from '../../entities/UnreachableCaseError';
import Body from '../primitive/Body';
import { Label } from '../primitive/Label';
import { getDateOpts } from '../../constants/dateOpts';
import {
    CLASS_PLANNER_CONTAINER,
    CLASS_PLANNER_DAY,
    CLASS_PLANNER_DAY_BLOCKED,
    CLASS_PLANNER_DAY_BOOKABLE,
    CLASS_PLANNER_DAY_BOOKABLE_OUTSIDE,
    CLASS_PLANNER_DAY_BOOKABLE_WITHIN,
    CLASS_PLANNER_DAY_SELECTED,
    CLASS_PLANNER_DAY_SELECTED_END,
    CLASS_PLANNER_DAY_SELECTED_START,
    CLASS_PLANNER_DAY_WITHIN,
} from '../../constants/customizationClassNames';

export type ColorVariant = 'primary' | 'secondary';

export type PlannerDayVariant =
    | 'disabled'
    | 'bookable'
    | 'bookable-soldout'
    | 'bookable-within'
    | 'bookable-outside'
    | 'default'
    | 'selected-start'
    | 'selected-end'
    | 'selected'
    | 'within'
    | 'loading'
    | 'blocked';

export interface PlannerDayProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: PlannerDayVariant;
    isSelecting?: boolean;
    label?: ReactNode;
    icon?: ReactNode;
    // TODO: we could probably make a theme of some sort to easily scale with other colors
    // periodColor?: ColorVariant;
    // defaultFontColor?: ColorVariant;
    date?: Date;
}

export const clickables: Array<PlannerDayVariant> = [
    'bookable',
    'bookable-soldout',
    'bookable-within',
    'bookable-outside',
    'selected-start',
    'selected-end',
    'selected',
];

export const hoverables: Array<PlannerDayVariant> = [
    'bookable',
    'bookable-within',
    'bookable-outside',
    'bookable-soldout',
];

const externalClassVariantMap: Partial<Record<PlannerDayVariant, string>> = {
    selected: CLASS_PLANNER_DAY_SELECTED,
    blocked: CLASS_PLANNER_DAY_BLOCKED,
    'selected-start': CLASS_PLANNER_DAY_SELECTED_START,
    'selected-end': CLASS_PLANNER_DAY_SELECTED_END,
    'bookable-outside': CLASS_PLANNER_DAY_BOOKABLE_OUTSIDE,
    'bookable-within': CLASS_PLANNER_DAY_BOOKABLE_WITHIN,
    within: CLASS_PLANNER_DAY_WITHIN,
    bookable: CLASS_PLANNER_DAY_BOOKABLE,
};

const PlannerDay: FC<React.PropsWithChildren<PlannerDayProps>> = memo(
    ({ label, children, icon, date, ...props }) => (
        <StyledBox className={CLASS_PLANNER_CONTAINER} id={date && format(date, 'yyyy-MM-dd', getDateOpts('nl'))}>
            {/* we only want bookable and selected days to be clickable */}
            <Container
                {...props}
                type="button"
                as={props.variant && clickables.includes(props.variant) ? undefined : 'span'}
            >
                <Wrapper
                    className={`${CLASS_PLANNER_DAY} ${props.variant ? externalClassVariantMap[props.variant] : ''}`}
                >
                    <Box>
                        {date && (
                            <>
                                <Flex alignItems="center" justifyContent="center">
                                    <Body style={{ font: 'inherit' }}>{format(date, 'd')}</Body>
                                    {icon}
                                </Flex>
                                {label && <Price>{label}</Price>}
                            </>
                        )}
                    </Box>
                </Wrapper>
            </Container>
        </StyledBox>
    ),
    (prev, next) => {
        if (prev.date && next.date && +prev.date !== +next.date) {
            return false;
        }

        // Only rerender if the day is subjected to change
        const relevantVariants = [...clickables, 'within'];
        if (next.variant && prev.variant === next.variant && prev.isSelecting !== next.isSelecting) {
            return !relevantVariants.includes(next.variant);
        }

        const ignoredKeys: Array<keyof PlannerDayProps> = [
            'date',
            'isSelecting',
            'onClick',
            'onMouseEnter',
            'onMouseDown',
            'onMouseLeave',
        ];

        return (Object.keys(prev) as Array<keyof PlannerDayProps>)
            .filter(key => !ignoredKeys.includes(key))
            .every(key => Object.is(prev[key], next[key]));
    }
);

export default PlannerDay;

export const Price = styled(Body).attrs({ variant: 'small' })`
    font-weight: 600;
`;

const Wrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
`;

const defaultCss = css`
    margin: 2px 0;
    color: ${({ theme }) => theme.colors.neutral['70']};
    ${({ theme }) => getFontCss(theme.fontSizes.body.regular)};
`;

const disabledCss = css`
    background: repeating-linear-gradient(
        -45deg,
        #eaeaea,
        #eaeaea 2px,
        rgba(235, 235, 235, 0) 2px,
        rgba(235, 235, 235, 0) 5px
    );
    margin-left: 2px;
    margin-right: 2px;
    color: ${({ theme }: { theme: Theme }) => theme.colors.neutral['30']};
    font-weight: 400;
`;

/**
 * Todo: clean up the conditional statements
 */
const bookableCss = css<PlannerDayProps>`
    color: ${({ theme }) => getAssignmentColor(theme, theme.colorAssignments.calendar.bookable.color)} !important;
    font-weight: 600;
    background: ${({ theme }) => getAssignmentColor(theme, theme.colorAssignments.calendar.bookable.backgroundColor)};
    margin-left: 2px;
    margin-right: 2px;

    :focus,
    :hover {
        color: ${({ theme }) =>
            getAssignmentColor(theme, theme.colorAssignments.calendar.bookable.focusColor)} !important;
    }
`;

const bookableSoldoutCss = css<PlannerDayProps>`
    color: ${({ theme }) => getAssignmentColor(theme, theme.colorAssignments.calendar.bookable.color)} !important;
    font-weight: 600;
    background: #ffffff;
    margin-left: 2px;
    margin-right: 2px;
    border: 2px #eaeaea solid;
`;

const selectedCss = css<PlannerDayProps>`
    color: ${({ theme }) => getAssignmentColor(theme, theme.colorAssignments.calendar.selected.color)} !important;
    font-weight: 600;
    border-radius: var(--borderRadius);

    background: ${({ theme }) =>
        getAssignmentColor(theme, theme.colorAssignments.calendar.selected.backgroundColor)} !important;

    :focus,
    :hover {
        --border-color: ${({ theme }) =>
            getAssignmentColor(theme, theme.colorAssignments.calendar.selected.focusColor)} !important;
    }

    svg {
        color: currentColor;
    }
`;
const selectedStartCss = css<PlannerDayProps>`
    ${selectedCss};
    border-radius: var(--borderRadius) 0 0 var(--borderRadius);
    margin-left: 2px;
`;

const selectedEndCss = css<PlannerDayProps>`
    ${selectedCss};
    border-radius: 0 var(--borderRadius) var(--borderRadius) 0;
    margin-right: 2px;
`;

const periodCss = css<PlannerDayProps>`
    ::after {
        content: '';
        background: ${({ theme, isSelecting }) =>
            isSelecting
                ? getAssignmentColor(theme, theme.colorAssignments.calendar.periodBackground.selected)
                : getAssignmentColor(theme, theme.colorAssignments.calendar.periodBackground.notSelected)} !important;
        position: absolute;
        z-index: -1;
        left: 0;
        top: 0;
        height: 100%;
        width: 100%;
        transition-property: background;
        transition-duration: 0.2s;
        transition-timing-function: ease-in;
    }
`;

const withinCss = css<PlannerDayProps>`
    ${periodCss};
    color: ${({ theme, isSelecting }) =>
        isSelecting
            ? getAssignmentColor(theme, theme.colorAssignments.calendar.withinCss.selected)
            : getAssignmentColor(theme, theme.colorAssignments.calendar.withinCss.notSelected)} !important;
    transition-property: color;
    transition-duration: 0.2s;
    transition-timing-function: ease-in;
`;

const bookableWithinCss = css<PlannerDayProps>`
    ${periodCss};
    ${Price} {
        color: inherit;
    }

    color: ${({ theme, isSelecting }) =>
        isSelecting
            ? getAssignmentColor(theme, theme.colorAssignments.calendar.bookableWithin.selected)
            : getAssignmentColor(theme, theme.colorAssignments.calendar.bookableWithin.notSelected)} !important;
    font-weight: 600;
`;

const bookableOutsideCss = css<PlannerDayProps>`
    ${Price} {
        color: ${({ theme }) => getAssignmentColor(theme, theme.colorAssignments.calendar.price)} !important};
    }

    color: ${({ theme }) => getAssignmentColor(theme, theme.colorAssignments.calendar.bookableOutside)} !important;
    font-weight: 600;
`;

const loadingCss = css`
    > div {
        width: 60%;
        height: 60%;
        border-radius: 0.8rem;
        background: ${({ theme }) => theme.colors.neutral['10']};
    }
`;

const blockedCss = css`
    ${disabledCss};
`;

const getCss = (variant: PlannerDayVariant) => {
    switch (variant) {
        case 'default':
            return defaultCss;
        case 'disabled':
            return disabledCss;
        case 'bookable':
            return bookableCss;
        case 'selected':
            return selectedCss;
        case 'selected-start':
            return selectedStartCss;

        case 'selected-end':
            return selectedEndCss;
        case 'within':
            return withinCss;
        case 'bookable-within':
            return bookableWithinCss;
        case 'bookable-outside':
            return bookableOutsideCss;
        case 'bookable-soldout':
            return bookableSoldoutCss;
        case 'loading':
            return loadingCss;
        case 'blocked':
            return blockedCss;
        default:
            throw new UnreachableCaseError(variant);
    }
};

const StyledBox = styled.div`
    width: calc(100% / 7);
    position: relative;

    ::after {
        content: '';
        padding-top: 100%;
        display: block;
        width: 100%;
        height: 0;
    }
`;

const Container = styled.button<Omit<PlannerDayProps, 'date'>>`
    --borderRadius: 4px;
    font: inherit;
    width: 100%;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0;
    /* padding-bottom: 100%; */
    height: 100%;
    outline: none;
    margin: 0;
    > ${Wrapper} {
        height: 100%;
        max-height: 100%;
        --border-color: transparant;
        border-radius: var(--borderRadius);
        font-family: ${({ theme }) => theme.fontFamily.body};
        background-clip: content-box;

        box-shadow: inset 0 0 0 2px var(--border-color);
        ${({ variant = 'default' }) => getCss(variant)};
    }

    > ${Label} {
        margin-top: 0;
    }

    svg {
        color: inherit;
        margin-left: 0.4rem;
    }
`;
